
'use client';

import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import type { CVData, PartialCVData, CVHardSkill, CVSocial } from '@/types';
import { db, storage } from '@/lib/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_CV_DOC_ID = 'main-cv';

const defaultCVData: CVData = {
    name: "Salaheddine ELMRABET",
    role: "3D Artist & Developer based in Morocco",
    portraitUrl: "https://placehold.co/400x400.png",
    bio: "Creative and technically skilled 3D Artist & Animator with 4+ years of experience in modeling, real-time rendering, and interactive asset development. Adept at bringing immersive environments and props to life using Maya, Blender, and real-time engines like Unity and Unreal Engine.",
    socials: [
        { id: "1", name: "email", url: "mailto:salahelmbt@gmail.com", iconUrl: "https://placehold.co/40x40/ffffff/1f2937?text=mail" },
        { id: "2", name: "linkedin", url: "#", iconUrl: "https://placehold.co/40x40/ffffff/1f2937?text=in" },
        { id: "3", name: "dribbble", url: "#", iconUrl: "https://placehold.co/40x40/ffffff/1f2937?text=db" },
    ],
    headerTags: [
        "#Branding #Logo",
        "#Social_Media #Poster",
        "#Packaging #Illustration",
        "#3D_&_Motion_Graphic"
    ],
    experience: [
        { id: "1", period: "2024–2025", role: "Creative Director / Developer", company: "L'idée - Interactive 3D Menu", tags: ["Unity", "3D", "UX/UI"] },
        { id: "2", period: "Ongoing", role: "Modder - Baldur’s Gate 3", company: "Larian Studios (Community)", tags: ["VFX", "Animation", "Props"] },
        { id: "3", period: "2021–Present", role: "Freelance 3D Artist", company: "Various NDA Clients", tags: ["Animation", "Product Visuals", "Retail"] },
        { id: "4", period: "2018-2021", role: "Diploma Student", company: "Studio M Casablanca", tags: ["Animation", "Game Design", "3D Modeling"] },
    ],
    expertise: "Logo, Brand Identity, Packaging, Poster & Editorial, Social Media Post, 3D & Motion Graphic",
    hardskills: [
        { id: "1", name: 'Maya', iconUrl: 'https://placehold.co/40x40/ffffff/1f2937?text=Ma' },
        { id: "2", name: 'Photoshop', iconUrl: 'https://placehold.co/40x40/ffffff/1f2937?text=Ps' },
        { id: "3", name: 'After Effects', iconUrl: 'https://placehold.co/40x40/ffffff/1f2937?text=Ae' },
        { id: "4", name: 'Illustrator', iconUrl: 'https://placehold.co/40x40/ffffff/1f2937?text=Ai' },
        { id: "5", name: 'Unity', iconUrl: 'https://placehold.co/40x40/ffffff/1f2937?text=Un' },
        { id: "6", name: 'Unreal', iconUrl: 'https://placehold.co/40x40/ffffff/1f2937?text=UE' },
    ],
    softskills: ["Creativity", "Teamwork", "Time Management", "Flexibility", "Communication"],
};


interface CVContextType {
    cv: CVData | null;
    loading: boolean;
    updateCV: (data: PartialCVData) => Promise<void>;
    uploadPortrait: (file: File) => Promise<void>;
    uploadHardSkillIcon: (skillId: string, file: File, currentSkills: CVHardSkill[]) => Promise<CVHardSkill | undefined>;
    uploadSocialIcon: (socialId: string, file: File) => Promise<CVSocial | undefined>;
}

const CVContext = createContext<CVContextType | undefined>(undefined);

export const CVProvider = ({ children }: { children: ReactNode }) => {
    const [cv, setCv] = useState<CVData | null>(null);
    const [loading, setLoading] = useState(true);
    const { isOwner } = useAuth();

    const fetchCVData = useCallback(async () => {
        setLoading(true);
        const cvRef = doc(db, 'cv', DEFAULT_CV_DOC_ID);
        try {
            const docSnap = await getDoc(cvRef);
            if (docSnap.exists()) {
                const data = docSnap.data();

                // Ensure socials is an array
                let socialsData: CVSocial[];
                if (Array.isArray(data.socials)) {
                    socialsData = data.socials;
                } else if (typeof data.socials === 'object' && data.socials !== null) {
                    // Convert old object format to new array format if needed
                    socialsData = Object.entries(data.socials).map(([key, value]: [string, any]) => ({
                        id: key, // Or generate new uuid
                        name: key,
                        url: typeof value === 'string' ? value : '#',
                        iconUrl: 'https://placehold.co/40x40/ffffff/1f2937?text=?',
                    }));
                } else {
                    socialsData = defaultCVData.socials;
                }
                
                const cvData: CVData = {
                    ...defaultCVData,
                    ...data,
                    socials: socialsData,
                };
                setCv(cvData);
            } else {
                if (isOwner) {
                    await setDoc(cvRef, defaultCVData);
                }
                setCv(defaultCVData);
            }
        } catch (error) {
            console.error("Failed to fetch CV data, using default.", error);
            setCv(defaultCVData);
        } finally {
            setLoading(false);
        }
    }, [isOwner]);

    useEffect(() => {
        fetchCVData();
    }, [fetchCVData]);

    const updateCV = async (data: PartialCVData) => {
        if (!isOwner) throw new Error("Not authorized");
        
        const cvRef = doc(db, 'cv', DEFAULT_CV_DOC_ID);
        await updateDoc(cvRef, data);
        setCv(prev => prev ? { ...prev, ...data } : null); // Update local state immediately
    };
    
    const uploadPortrait = async (file: File) => {
        if (!isOwner || !cv) throw new Error("Not authorized or CV not loaded");

        const cvRef = doc(db, 'cv', DEFAULT_CV_DOC_ID);
        
        let portraitUrl = cv.portraitUrl;
        let portraitPath = cv.portraitPath;

        // Delete old image if it exists
        if (portraitPath) {
            try { await deleteObject(ref(storage, portraitPath)); }
            catch (e) { console.warn("Old portrait not found, could not delete.")}
        }
        // Upload new image
        portraitPath = `cv/portrait-${uuidv4()}`;
        const storageRef = ref(storage, portraitPath);
        await uploadBytes(storageRef, file);
        portraitUrl = await getDownloadURL(storageRef);

        const updates = { portraitUrl, portraitPath };
        await updateDoc(cvRef, updates);
        setCv(prev => prev ? { ...prev, ...updates } : null);
    }
    
    const uploadHardSkillIcon = async (skillId: string, file: File, currentSkills: CVHardSkill[]): Promise<CVHardSkill | undefined> => {
        if (!isOwner || !cv) throw new Error("Not authorized or CV not loaded");
        
        const skillToUpdate = currentSkills.find(s => s.id === skillId);
        if (!skillToUpdate) throw new Error("Skill not found");

        // Delete old icon if it exists
        if (skillToUpdate.iconPath) {
            try { await deleteObject(ref(storage, skillToUpdate.iconPath)); }
            catch (e) { console.warn("Old icon not found, could not delete.")}
        }

        // Upload new icon
        const iconPath = `cv/hardskills/${skillId}-${uuidv4()}`;
        const storageRef = ref(storage, iconPath);
        await uploadBytes(storageRef, file);
        const iconUrl = await getDownloadURL(storageRef);

        const updatedSkill = { ...skillToUpdate, iconUrl, iconPath };
        
        const updatedHardSkills = currentSkills.map(s => s.id === skillId ? updatedSkill : s);

        await updateCV({ hardskills: updatedHardSkills });
        
        return updatedSkill;
    }

    const uploadSocialIcon = async (socialId: string, file: File): Promise<CVSocial | undefined> => {
        if (!isOwner || !cv) throw new Error("Not authorized or CV not loaded");
        
        const socialToUpdate = cv.socials.find(s => s.id === socialId);
        if (!socialToUpdate) throw new Error("Social link not found");

        if (socialToUpdate.iconPath) {
            try { await deleteObject(ref(storage, socialToUpdate.iconPath)); }
            catch (e) { console.warn("Old social icon not found, could not delete.")}
        }

        const iconPath = `cv/socials/${socialId}-${uuidv4()}`;
        const storageRef = ref(storage, iconPath);
        await uploadBytes(storageRef, file);
        const iconUrl = await getDownloadURL(storageRef);

        const updatedSocial = { ...socialToUpdate, iconUrl, iconPath };
        const updatedSocials = cv.socials.map(s => s.id === socialId ? updatedSocial : s);
        await updateCV({ socials: updatedSocials });
        
        return updatedSocial;
    }
    
    return (
        <CVContext.Provider value={{ cv, loading, updateCV, uploadPortrait, uploadHardSkillIcon, uploadSocialIcon }}>
            {children}
        </CVContext.Provider>
    );
};

export const useCV = () => {
    const context = useContext(CVContext);
    if (context === undefined) {
        throw new Error('useCV must be used within a CVProvider');
    }
    return context;
};
